import React from 'react';
import qs from 'qs';
import { z } from 'zod';
import { useNavigate } from 'react-router';
import {
  Layout,
  useApi,
  Gutter,
  FormLabel,
  ContextMenu,
  ContextMenuItem,
  useBreakpoint,
  Icon,
  LayoutBleed,
  Input,
  Form,
  IconButton,
  Grid,
  GridRow,
  GridColumn,
  LayoutItem,
  HeaderLink,
} from '@axiom/ui';
import type { Taxonomy } from '@axiom/validation';
import type { FormPropsChildren } from '@axiom/ui';

import { TaxonomyApi } from '../../api/taxonomy-api';

const handleKeyDown = (
  event: React.KeyboardEvent<HTMLDivElement>,
  fireSubmit: FormPropsChildren<unknown>['fireSubmit']
) => {
  if (event.key.toLowerCase() === 'enter') {
    void fireSubmit();
  }
};

type SubNavLinkConfig = Array<{
  name: string;
  link: {
    search?: string;
    practiceAreas?: Array<keyof Taxonomy['practiceAreas']>;
    businessTitles?: Array<keyof Taxonomy['businessTitles']>;
  };
}>;

export type SubNavigationHeaderProps = {
  name?: string;
};

export const SubNavigationHeader = ({
  name,
}: SubNavigationHeaderProps = {}) => {
  const [{ data: taxonomy }] = useApi(TaxonomyApi.readTaxonomy());
  const { isMobile, isLargeScreen, isTablet } = useBreakpoint();
  const navigate = useNavigate();

  const SearchSchema = z.object({
    SEARCH_ALT: z.string().nullable(),
  });

  /**
   * Focus Areas get search from fullName
   * Practice Areas get query param link
   * Alphabetize by title
   */
  const subNavLinks: SubNavLinkConfig = Object.entries(
    taxonomy.clientSubNavigationHeader
  )
    .map(([_, nav]) => ({
      name: nav.title,
      link: {
        ...(nav.keyword ? { search: nav.keyword } : {}),
        ...(nav.practiceArea ? { practiceAreas: [nav.practiceArea] } : {}),
        ...(nav.businessTitle ? { businessTitles: [nav.businessTitle] } : {}),
      },
    }))
    .sort((a, b) => a.name.localeCompare(b.name));

  const navigateToALTPage = (link: unknown) => {
    void navigate(
      `/browse-talent/page/1${qs.stringify(link, {
        addQueryPrefix: true,
      })}`
    );
  };
  return (
    <div data-test={name}>
      <Form
        schema={SearchSchema}
        name="SUB_NAVIGATION_FORM"
        initialValues={{ SEARCH_ALT: '' }}
        onSubmit={formData => {
          if (!formData.SEARCH_ALT) {
            return;
          }
          navigateToALTPage({ search: `"${formData.SEARCH_ALT}"` });
        }}
      >
        {({ fireSubmit }) => {
          return (
            <Layout
              background="panelLight"
              name="SUB_NAVIGATION"
              borderBottom="1px solid contentBreak"
            >
              <LayoutBleed>
                <Gutter vertical="16px">
                  {isLargeScreen ? (
                    <Grid>
                      <GridRow>
                        <GridColumn width={9}>
                          <Layout
                            horizontalGutter="16px"
                            position="middle"
                            name="LARGE_SCREEN_LINKS"
                          >
                            <FormLabel>Explore Practice Areas:</FormLabel>
                            {subNavLinks.slice(0, 4).map((item, index) => (
                              <div
                                key={`${item.name}_${index + 1}`}
                                onClick={() => {
                                  navigateToALTPage(item.link);
                                }}
                              >
                                <HeaderLink name={`${item.name}_LINK`}>
                                  {item.name}
                                </HeaderLink>
                              </div>
                            ))}
                            <ContextMenu
                              name="LARGE_SCREEN_CONTEXT_MENU"
                              anchor={
                                <>
                                  <Gutter right="8px">More</Gutter>
                                  <Icon name="arrow-down" />
                                </>
                              }
                            >
                              {subNavLinks.slice(4).map((item, index) => (
                                <ContextMenuItem
                                  name={`${item.name}_LINK`}
                                  key={`${item.name}_${index + 1}`}
                                  onClick={() => {
                                    navigateToALTPage(item.link);
                                  }}
                                >
                                  {item.name}
                                </ContextMenuItem>
                              ))}
                            </ContextMenu>
                          </Layout>
                        </GridColumn>
                        <GridColumn width={3}>
                          <div
                            onKeyDown={(
                              e: React.KeyboardEvent<HTMLDivElement>
                            ) => {
                              handleKeyDown(e, fireSubmit);
                            }}
                          >
                            <Layout
                              position="middle"
                              horizontalGutter="8px"
                              name="SEARCH_INPUT_LAYOUT"
                              stretched
                            >
                              <LayoutItem fluid>
                                <Input
                                  placeholder="Talent Search"
                                  name="SEARCH_ALT"
                                />
                              </LayoutItem>
                              <IconButton
                                name="SEARCH_BUTTON"
                                icon="search"
                                onClick={fireSubmit}
                              />
                            </Layout>
                          </div>
                        </GridColumn>
                      </GridRow>
                    </Grid>
                  ) : null}
                  {isTablet ? (
                    <Grid>
                      <GridRow>
                        <GridColumn width={8}>
                          <Layout
                            horizontalGutter="16px"
                            position="middle"
                            name="TABLET_SCREEN_LINKS"
                          >
                            {subNavLinks.slice(0, 2).map((item, index) => (
                              <div
                                key={`${item.name}_${index + 1}`}
                                onClick={() => {
                                  navigateToALTPage(item.link);
                                }}
                              >
                                <HeaderLink name={`${item.name}_LINK`}>
                                  {item.name}
                                </HeaderLink>
                              </div>
                            ))}
                            <ContextMenu
                              name="TABLET_SCREEN_CONTEXT_MENU"
                              anchor={
                                <>
                                  <Gutter right="8px">More</Gutter>
                                  <Icon name="arrow-down" />
                                </>
                              }
                            >
                              {subNavLinks.slice(2).map((item, index) => (
                                <ContextMenuItem
                                  name={`${item.name}_LINK`}
                                  key={`${item.name}_${index + 1}`}
                                  onClick={() => {
                                    navigateToALTPage(item.link);
                                  }}
                                >
                                  {item.name}
                                </ContextMenuItem>
                              ))}
                            </ContextMenu>
                          </Layout>
                        </GridColumn>
                        <GridColumn width={4}>
                          <div
                            onKeyDown={(
                              e: React.KeyboardEvent<HTMLDivElement>
                            ) => {
                              handleKeyDown(e, fireSubmit);
                            }}
                          >
                            <Layout
                              position="middle"
                              horizontalGutter="8px"
                              name="SEARCH_INPUT_LAYOUT"
                              stretched
                            >
                              <LayoutItem fluid>
                                <Input
                                  placeholder="Talent Search"
                                  name="SEARCH_ALT"
                                />
                              </LayoutItem>
                              <IconButton
                                name="SEARCH_BUTTON"
                                icon="search"
                                onClick={fireSubmit}
                              />
                            </Layout>
                          </div>
                        </GridColumn>
                      </GridRow>
                    </Grid>
                  ) : null}
                  {isMobile ? (
                    <Layout
                      horizontalGutter="16px"
                      position="middle"
                      name="MOBILE_SCREEN_LINKS"
                    >
                      <FormLabel>Explore Practice Areas:</FormLabel>
                      <ContextMenu
                        name="MOBILE_SCREEN_CONTEXT_MENU"
                        direction="left"
                        anchor={
                          <>
                            <Gutter right="8px">All</Gutter>
                            <Icon name="arrow-down" />
                          </>
                        }
                      >
                        {subNavLinks.map((item, index) => (
                          <ContextMenuItem
                            name={`${item.name}_LINK`}
                            key={`${item.name}_${index + 1}`}
                            onClick={() => {
                              navigateToALTPage(item.link);
                            }}
                          >
                            {item.name}
                          </ContextMenuItem>
                        ))}
                      </ContextMenu>
                    </Layout>
                  ) : null}
                </Gutter>
              </LayoutBleed>
            </Layout>
          );
        }}
      </Form>
    </div>
  );
};
